import {
  Button,
  Checkbox,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Space,
  Tag,
  theme,
  message,
} from "antd";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { useMemo, useState } from "react";
import { ScheduleOperation } from "../../../serviceoperation/ScheduleOperation";
import {
  ScheduleDetails,
  UpsertScheduleTaskOnSchedule,
} from "../../../serviceoperation/entity/ScheduleOperationEntity";

interface TaskFormValue {
  taskId?: string;
  name?: string;
  description?: string;
  category?: string;
  estimatedMinutes?: number;
  requirePhoto?: boolean;
  requireNote?: boolean;
  isDeleted?: boolean;
}

interface TaskManagerFormValue {
  tasks?: TaskFormValue[];
}

interface ScheduleTaskManagerModalProps {
  open: boolean;
  corporationId: number;
  scheduleDetail: ScheduleDetails;
  onCancel: () => void;
  onSaved: () => void;
}

const ScheduleTaskManagerModal: React.FC<ScheduleTaskManagerModalProps> = ({
  open,
  corporationId,
  scheduleDetail,
  onCancel,
  onSaved,
}) => {
  const { token } = theme.useToken();
  const [form] = Form.useForm<TaskManagerFormValue>();
  const [saving, setSaving] = useState(false);

  const initialTasks = useMemo(() => {
    return [...(scheduleDetail.tasks ?? [])]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((task) => ({
        taskId: task.id,
        name: task.name,
        description: task.description,
        category: task.category,
        estimatedMinutes: task.estimatedMinutes,
        requirePhoto: task.requirePhoto,
        requireNote: task.requireNote,
        isDeleted: false,
      }));
  }, [scheduleDetail.tasks]);

  const handleRemoveOrMarkDelete = (
    fieldIndex: number,
    remove: (index: number) => void,
  ) => {
    const existingTasks = form.getFieldValue("tasks") as
      | TaskFormValue[]
      | undefined;
    const tasks = [...(existingTasks ?? [])];
    const current = tasks[fieldIndex];
    if (!current) return;

    if (current.taskId) {
      tasks[fieldIndex] = {
        ...current,
        isDeleted: true,
      };
      form.setFieldsValue({ tasks });
      return;
    }

    remove(fieldIndex);
  };

  const handleUndoDelete = (fieldIndex: number) => {
    const existingTasks = form.getFieldValue("tasks") as
      | TaskFormValue[]
      | undefined;
    const tasks = [...(existingTasks ?? [])];
    const current = tasks[fieldIndex];
    if (!current) return;

    tasks[fieldIndex] = {
      ...current,
      isDeleted: false,
    };
    form.setFieldsValue({ tasks });
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const formTasks = (values.tasks ?? [])
        .map((task, index) => ({
          taskId: task.taskId?.trim() || undefined,
          name: (task.name ?? "").trim(),
          description: task.description?.trim() || undefined,
          category: task.category?.trim() || undefined,
          estimatedMinutes: Number(task.estimatedMinutes ?? 5),
          requirePhoto: !!task.requirePhoto,
          requireNote: !!task.requireNote,
          sortOrder: index,
          isDeleted: !!task.isDeleted,
        }))
        .filter((task) => {
          if (task.isDeleted) {
            return !!task.taskId;
          }
          return task.name.length > 0;
        });

      const initialTaskMap = new Map(
        initialTasks
          .filter((task) => !!task.taskId)
          .map((task, index) => [
            task.taskId as string,
            {
              name: (task.name ?? "").trim(),
              description: task.description?.trim() || undefined,
              category: task.category?.trim() || undefined,
              estimatedMinutes: Number(task.estimatedMinutes ?? 5),
              requirePhoto: !!task.requirePhoto,
              requireNote: !!task.requireNote,
              sortOrder: index,
              isDeleted: !!task.isDeleted,
            },
          ]),
      );

      const changedTasks = formTasks.filter((task) => {
        if (!task.taskId) {
          return true;
        }

        const initial = initialTaskMap.get(task.taskId);
        if (!initial) {
          return true;
        }

        return (
          task.name !== initial.name ||
          task.description !== initial.description ||
          task.category !== initial.category ||
          task.estimatedMinutes !== initial.estimatedMinutes ||
          task.requirePhoto !== initial.requirePhoto ||
          task.requireNote !== initial.requireNote ||
          task.sortOrder !== initial.sortOrder ||
          task.isDeleted !== initial.isDeleted
        );
      });

      if (changedTasks.length === 0) {
        message.info("No task changes to save.");
        return;
      }

      const upsertTasks: UpsertScheduleTaskOnSchedule[] = changedTasks.map(
        (task) => ({
          TaskId: task.taskId,
          Name: task.name,
          Description: task.description,
          Category: task.category,
          EstimatedMinutes: task.estimatedMinutes,
          RequirePhoto: task.requirePhoto,
          RequireNote: task.requireNote,
          SortOrder: task.sortOrder,
          IsDeleted: task.isDeleted || undefined,
        }),
      );

      const scheduleOps = new ScheduleOperation();
      setSaving(true);

      const response = await scheduleOps.putScheduleAsync(
        corporationId,
        scheduleDetail.scheduleID,
        {
          WorkDay: new Date(scheduleDetail.workDate),
          StartWorkHour: scheduleDetail.startWorkHour,
          StartWorkMinute: scheduleDetail.startWorkMinute,
          EndWorkHour: scheduleDetail.endWorkHour,
          EndWorkMinute: scheduleDetail.endWorkMinute,
          Tasks: upsertTasks,
        },
      );

      if (response.status === 204 || response.status === 200) {
        message.success("Schedule tasks updated successfully.");
        onSaved();
        return;
      }

      message.error("Unable to update schedule tasks. Please try again.");
    } catch {
      message.error("Unable to update schedule tasks. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Manage Schedule Tasks"
      okText="Save Tasks"
      onCancel={onCancel}
      onOk={handleSave}
      confirmLoading={saving}
      width={840}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ tasks: initialTasks }}
        preserve={false}
      >
        <Form.List name="tasks">
          {(fields, { add, remove }) => (
            <Space direction="vertical" style={{ width: "100%" }} size={12}>
              {fields.map((field) => (
                <div
                  key={field.key}
                  style={{
                    border: `1px solid ${token.colorBorderSecondary}`,
                    borderRadius: 8,
                    padding: 12,
                  }}
                >
                  <Row gutter={[12, 8]}>
                    <Col span={24}>
                      <Form.Item name={[field.name, "taskId"]} hidden>
                        <Input />
                      </Form.Item>
                      <Form.Item name={[field.name, "isDeleted"]} hidden>
                        <Input />
                      </Form.Item>
                    </Col>
                    <Form.Item
                      noStyle
                      shouldUpdate={(prev, next) =>
                        prev.tasks?.[field.name]?.isDeleted !==
                        next.tasks?.[field.name]?.isDeleted
                      }
                    >
                      {({ getFieldValue }) => {
                        const isDeleted = !!getFieldValue([
                          "tasks",
                          field.name,
                          "isDeleted",
                        ]);

                        return (
                          <>
                            {isDeleted ? (
                              <Col span={24}>
                                <Space
                                  align="center"
                                  style={{
                                    width: "100%",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <Tag color="error">Marked for deletion</Tag>
                                  <Button
                                    size="small"
                                    onClick={() => {
                                      handleUndoDelete(field.name);
                                    }}
                                  >
                                    Undo delete
                                  </Button>
                                </Space>
                              </Col>
                            ) : null}
                          </>
                        );
                      }}
                    </Form.Item>
                    <Col span={10}>
                      <Form.Item
                        name={[field.name, "name"]}
                        label="Task Name"
                        rules={[
                          { required: true, message: "Task name is required" },
                        ]}
                      >
                        <Input placeholder="Task name" maxLength={150} />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item
                        name={[field.name, "estimatedMinutes"]}
                        label="Minutes"
                        rules={[
                          { required: true, message: "Minutes required" },
                        ]}
                      >
                        <InputNumber
                          min={1}
                          max={480}
                          style={{ width: "100%" }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        name={[field.name, "category"]}
                        label="Category"
                      >
                        <Input placeholder="Optional" maxLength={50} />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Form.Item
                        name={[field.name, "description"]}
                        label="Description"
                      >
                        <Input.TextArea rows={2} maxLength={500} />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Space size={18} wrap>
                        <Form.Item
                          name={[field.name, "requirePhoto"]}
                          valuePropName="checked"
                          style={{ marginBottom: 0 }}
                        >
                          <Checkbox>Require photo</Checkbox>
                        </Form.Item>
                        <Form.Item
                          name={[field.name, "requireNote"]}
                          valuePropName="checked"
                          style={{ marginBottom: 0 }}
                        >
                          <Checkbox>Require note</Checkbox>
                        </Form.Item>
                      </Space>
                    </Col>
                    <Col span={24}>
                      <Button
                        danger
                        icon={<MinusCircleOutlined />}
                        onClick={() =>
                          handleRemoveOrMarkDelete(field.name, remove)
                        }
                      >
                        Delete Task
                      </Button>
                    </Col>
                  </Row>
                </div>
              ))}

              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={() =>
                  add({
                    estimatedMinutes: 15,
                    requirePhoto: false,
                    requireNote: false,
                  })
                }
                block
              >
                Add Task
              </Button>
            </Space>
          )}
        </Form.List>
      </Form>
    </Modal>
  );
};

export default ScheduleTaskManagerModal;
